import {
  App,
  Button,
  DatePicker,
  Empty,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { academicsApi, type ExamSchedule } from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  subjectId: number;
  examDate: Dayjs;
  totalMarks: number;
  passingMarks: number;
}

export function ExamSchedulePage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ACADEMICS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('ACADEMICS:DELETE'));
  const academicYearId = useAuthStore((s) => s.academicYearId);

  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [examTypeId, setExamTypeId] = useState<number | undefined>(undefined);
  const [editing, setEditing] = useState<ExamSchedule | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: sections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    staleTime: 60_000,
  });

  const { data: examTypes } = useQuery({
    queryKey: ['setup', 'lookups', 'exam-types', 'active'],
    queryFn: () => setupApi.listLookup('exam-types', true),
    staleTime: 5 * 60_000,
  });

  const { data: classSubjects } = useQuery({
    queryKey: ['academics', 'class-subjects', classSectionId],
    queryFn: () => academicsApi.listClassSubjects(classSectionId!),
    enabled: classSectionId != null,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['academics', 'exam-schedule', classSectionId, examTypeId],
    queryFn: () => academicsApi.examSchedule(classSectionId!, examTypeId),
    enabled: classSectionId != null && examTypeId != null,
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) => {
      const body = {
        classSectionId: classSectionId!,
        examTypeId: examTypeId!,
        subjectId: v.subjectId,
        examDate: v.examDate.format('YYYY-MM-DD'),
        totalMarks: v.totalMarks,
        passingMarks: v.passingMarks,
      };
      return editing
        ? academicsApi.updateExamSchedule(editing.id, body)
        : academicsApi.createExamSchedule(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Schedule updated' : 'Exam scheduled');
      qc.invalidateQueries({ queryKey: ['academics', 'exam-schedule'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => academicsApi.deleteExamSchedule(id),
    onSuccess: () => {
      message.success('Exam removed');
      qc.invalidateQueries({ queryKey: ['academics', 'exam-schedule'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ totalMarks: 100, passingMarks: 33 });
  };
  const openEdit = (row: ExamSchedule) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      subjectId: row.subjectId,
      examDate: dayjs(row.examDate),
      totalMarks: row.totalMarks,
      passingMarks: row.passingMarks,
    });
  };
  const open = creating || editing != null;

  const usedSubjectIds = new Set((data ?? []).map((r) => r.subjectId));
  const subjectOptions = (classSubjects ?? [])
    .filter((cs) => !editing ? !usedSubjectIds.has(cs.subjectId) : cs.subjectId === editing.subjectId)
    .map((cs) => ({ value: cs.subjectId, label: cs.subjectName ?? '' }));

  const columns: TableProps<ExamSchedule>['columns'] = [
    {
      title: 'Subject',
      dataIndex: 'subjectName',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Date',
      dataIndex: 'examDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Total / Passing',
      width: 160,
      align: 'right',
      render: (_, row) => `${row.passingMarks} / ${row.totalMarks}`,
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
          {canDelete && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                modal.confirm({
                  title: `Remove exam for ${row.subjectName}?`,
                  okType: 'danger',
                  onOk: () => remove.mutate(row.id),
                })
              }
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Exam schedule"
        description="Schedule each subject's exam date and marks for a section + exam type."
        count={classSectionId && examTypeId && data ? { value: data.length, label: 'subjects' } : undefined}
        actions={
          canCreate && classSectionId != null && examTypeId != null ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Schedule subject
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Class section"
          style={{ minWidth: 320 }}
          value={classSectionId}
          onChange={(v) => setClassSectionId(v)}
          options={(sections ?? []).map((s) => ({
            value: s.id,
            label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
          }))}
        />
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Exam type"
          style={{ minWidth: 200 }}
          value={examTypeId}
          onChange={(v) => setExamTypeId(v)}
          options={(examTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />
      </Space>

      {classSectionId == null || examTypeId == null ? (
        <Empty description="Pick a section and exam type to view its schedule" />
      ) : data && data.length === 0 && !isLoading ? (
        <Empty description="No subjects scheduled yet for this section + exam type" />
      ) : (
        <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={false} />
      )}

      <Modal
        title={editing ? `Edit · ${editing.subjectName}` : 'Schedule a subject'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Schedule'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Subject" name="subjectId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                disabled={!!editing}
                options={subjectOptions}
                notFoundContent={<Typography.Text type="secondary">All section subjects already scheduled.</Typography.Text>}
              />
            </Form.Item>
            <Form.Item label="Exam date" name="examDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Total marks"
                name="totalMarks"
                rules={[{ required: true, type: 'number', min: 1 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={1000} step={5} />
              </Form.Item>
              <Form.Item
                label="Passing marks"
                name="passingMarks"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={1000} step={1} />
              </Form.Item>
            </Space.Compact>
          </Form>
        )}
      </Modal>
    </div>
  );
}
